import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Picture } from '@/components/media/Picture';
import { brandMark } from '@/lib/media/case-images';
import { APP_VERSION, APP_VERSION_DISPLAY } from '@/lib/app-version';
import { brand, socialProfiles } from '@/config/brand';

const GRAD = '#5048E5';
const GRAD_TEXT: React.CSSProperties = {
  background: GRAD,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

export default function Footer() {
  const year = new Date().getFullYear();
  // Version is a UTC ISO stamp; localising it produces different text on the
  // server (UTC) vs the browser (local TZ), which caused a hydration mismatch
  // that regenerated the whole tree on load (the "flash"). Render the local
  // string only after mount so SSR and first client paint stay identical.
  const [versionLabel, setVersionLabel] = useState<string | null>(null);
  useEffect(() => {
    if (APP_VERSION !== 'dev') setVersionLabel(APP_VERSION_DISPLAY);
  }, []);

  return (
    <footer style={{ background: 'hsl(var(--card))', borderTop: '1px solid hsl(var(--border))' }}>
      <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-4">

          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" className="mb-5 inline-flex items-center gap-3">
              <Picture
                {...brandMark}
                alt=""
                aria-hidden="true"
                className="h-10 w-10 shrink-0 rounded-xl object-cover"
                width={40}
                height={40}
                loading="lazy"
              />
              <span className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'hsl(var(--foreground))' }}>
                {brand.name}
              </span>
            </Link>
            <p className="mb-5 max-w-xs text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'hsl(var(--muted-foreground))' }}>
              {brand.voice.footerBlurb}
            </p>
            <span className="text-sm font-semibold" style={{ ...GRAD_TEXT, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {brand.voice.footerTagline}
            </span>
            <p className="mt-4 text-xs" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'hsl(var(--muted-foreground))' }}>
              {brand.legalName}{brand.companyNumber ? ` \u00A0·\u00A0 Company No. ${brand.companyNumber}` : ''}
            </p>

            {/* Social / directory profiles — driven by src/config/brand.ts.
                Each link only appears once its URL is set there, so the Google
                Business Profile stays hidden until the placeholder is filled. */}
            {socialProfiles.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2">
                {socialProfiles.map(({ key, label, url }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm transition-colors hover:opacity-100"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'hsl(var(--muted-foreground))' }}
                  >
                    {label}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <p className="mb-5 text-xs font-bold uppercase tracking-widest" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'hsl(var(--muted-foreground))' }}>
              Quick Links
            </p>

            <div className="flex flex-col gap-3">
              {[
                { href: '/about',                label: 'About' },
                { href: '/services',             label: 'Services' },
                { href: '/case-studies',         label: 'Case Studies' },
                { href: '/blog',                 label: 'Blog' },
                { href: '/faq',                  label: 'FAQ' },
                { href: '/careers',              label: 'Careers' },
                { href: '/contact',              label: 'Contact Us' },
              ].map(({ href, label }) => (
                <Link
                  key={href}
                  to={href}
                  className="text-sm transition-colors hover:opacity-100"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'hsl(var(--muted-foreground))' }}
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>

          {/* Legal */}
          <div>
            <p className="mb-5 text-xs font-bold uppercase tracking-widest" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'hsl(var(--muted-foreground))' }}>
              Legal
            </p>

            <div className="flex flex-col gap-3">
              {[
                { href: '/privacy-policy',   label: 'Privacy Policy' },
                { href: '/terms-of-service', label: 'Terms of Service' },
              ].map(({ href, label }) => (
                <Link
                  key={href}
                  to={href}
                  className="text-sm transition-colors hover:opacity-100"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'hsl(var(--muted-foreground))' }}
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{ borderTop: '1px solid hsl(var(--border))' }}>
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-5 md:flex-row">
          <p className="text-xs" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'hsl(var(--muted-foreground))' }}>
            © {year} {brand.legalName}. All rights reserved.
          </p>
          {versionLabel && (
            <span
              className="text-[10px] leading-tight"
              style={{ color: 'hsl(var(--muted-foreground))', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              v{versionLabel}
            </span>
          )}
        </div>
      </div>
    </footer>
  );
}
